import { useLingui } from "@lingui/react/macro";
import type { Bot } from "@rakazo/contracts";
import { workspaceFileChatDestination } from "@rakazo/contracts";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { rpc, selectedSpaceId, selectSpace } from "../lib/rpc";
import { errorText } from "../lib/user-error";

type EchoState = {
  bot: Bot | null;
  error: string;
  refresh: () => void;
  update: (bot: Bot) => void;
  open: (fromPath?: string, windowId?: string) => void;
  returnToWork: (() => void) | null;
};
const Context = createContext<EchoState | null>(null);

/** One account identity across working screens. No polling or separate assistant runtime. */
export function EchoProvider({ children }: { children: ReactNode }) {
  const { t } = useLingui();
  const location = useLocation();
  const navigate = useNavigate();
  const [origin, setOrigin] = useState<{ path: string; spaceId: string; windowId?: string } | null>(
    null,
  );
  const [bot, setBot] = useState<Bot | null>(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  // Onboarding owns first-bot creation. Starting the account assistant there
  // can win that race and change the initial conversation and roster order.
  const ready = location.pathname !== "/onboarding";
  const generation = useRef(0);
  const update = useCallback((value: Bot) => {
    generation.current++;
    setBot(value);
    setError("");
  }, []);
  useEffect(() => {
    if (!ready) return;
    let live = true;
    let pending = false;
    const load = async () => {
      if (pending || document.hidden) return;
      pending = true;
      const current = ++generation.current;
      try {
        const value = await rpc.echo.ensure();
        if (live && current === generation.current) {
          setBot(value);
          setError("");
        }
      } catch (e) {
        if (live && current === generation.current) setError(errorText(e));
      } finally {
        pending = false;
      }
    };
    void load();
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", load);
    return () => {
      live = false;
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", load);
    };
  }, [revision, ready]);
  function open(fromPath = `${location.pathname}${location.search}`, windowId?: string) {
    if (!bot) return;
    const spaceId = selectedSpaceId();
    const document = workspaceFileChatDestination(fromPath, "web", spaceId, bot);
    if (!selectSpace(bot.spaceId)) {
      setError(t`Could not switch space`);
      return;
    }
    if (fromPath.split("?")[0] !== `/app/${bot.id}`)
      setOrigin({ path: fromPath, spaceId: spaceId ?? bot.spaceId, windowId });
    navigate(document?.webPath ?? `/app/${bot.id}`, { state: { workspaceOpen: "resource" } });
  }
  function returnToWork() {
    if (!origin) return;
    if (!selectSpace(origin.spaceId)) {
      setError(t`Could not switch space`);
      return;
    }
    navigate(origin.path, { state: { workspaceWindowId: origin.windowId } });
    setOrigin(null);
  }
  return (
    <Context.Provider
      value={{
        bot,
        error,
        refresh: () => setRevision((n) => n + 1),
        update,
        open,
        returnToWork: origin ? returnToWork : null,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function useEcho() {
  const context = useContext(Context);
  if (!context) throw new Error("EchoProvider is required inside the authenticated workspace");
  return context;
}
